import {
  makeStyles,
  Button,
  Dialog,
  DialogActionArrows,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Grid
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, DialogProps } from '@paintscout/ui'
import { OverridableTextArea, useClientOptions } from '@paintscout/ui-business'
import type { QuoteItemSection, UpdateableSubstrate } from '@paintscout/util/builder'
import { findItem, getItemSection, getObjectLabels, updateSubstrate } from '@paintscout/util/builder'
import type { QuoteDocument } from 'paintscout'
import React, { useRef, useEffect } from 'react'
import { isLooselyEqual } from '@paintscout/util'
import { useQuote } from '../../context'

export interface EditSubstrateDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  quote: QuoteDocument
  item: UpdateableSubstrate
  loading?: boolean
  nextItemKey?: string
  previousItemKey?: string
  section?: QuoteItemSection
  cancelButtonLabel?: string
  onConfirm: (updatedQuote: QuoteDocument, itemKey?: string) => void
  onCancel: (quote: QuoteDocument, isDirty?: boolean) => void
}

export const useStyles = makeStyles<Theme, EditSubstrateDialogProps>((theme) => {
  return {
    root: {},
    content: {},
    contentInner: {},

    labelInput: {
      fontWeight: theme.typography.fontWeightMedium
    },
    htmlEditor: {},
    backButton: {
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          backgroundColor: 'transparent !important',
          color: `${theme.palette.primary.main} !important`
        }
      }
    }
  }
})

function EditSubstrateDialog(props: EditSubstrateDialogProps) {
  const {
    item,
    quote,
    open,
    loading,
    nextItemKey,
    previousItemKey,
    onCancel,
    onConfirm,
    section,
    cancelButtonLabel,
    ...baseDialogProps
  } = props
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const { isEditable } = useQuote()
  const objectLabels = getObjectLabels({ options })

  // Setup details
  const substrateKey = item.key
  const substrateRef = useRef<UpdateableSubstrate>(null)
  substrateRef.current = findItem({ quote, options, key: substrateKey, section }) as UpdateableSubstrate

  const nameRef = useRef(null)
  const descriptionRef = useRef(null)

  const fullSubstrate = quote.substrates[substrateKey]
  const substrateSection = getItemSection(fullSubstrate)
  const isSplitSection = substrateSection !== section
  const hasCustomDescription = fullSubstrate?.area_string?.use_custom ?? false
  const hasCustomName = fullSubstrate?.clientLabel?.useCustom ?? false
  const isSmartTemplate = quote.type === 'quoteTemplate' && quote?.smartTemplate
  const splitCustom = isSplitSection && (hasCustomDescription || hasCustomName)
  const disabled =
    isSmartTemplate || splitCustom || (!isEditable && ['bid', 'additional', 'added-options'].includes(section))

  // Sync details on dialog swap with arrows
  useEffect(() => {
    const updatedSubstrate = {
      ...(findItem({ quote, options, key: substrateKey, section }) as UpdateableSubstrate)
    }
    const currentDescriptionValue = updatedSubstrate.substrateDescription.useCustom
      ? updatedSubstrate.substrateDescription.custom
      : updatedSubstrate.substrateDescription.default
    const currentLabelValue = updatedSubstrate.clientLabel.useCustom
      ? updatedSubstrate.clientLabel.custom
      : updatedSubstrate.clientLabel.default
    substrateRef.current = updatedSubstrate
    descriptionRef?.current?.setValue(currentDescriptionValue)
    nameRef?.current?.setValue(currentLabelValue)
  }, [substrateKey, item, item.key, quote])

  // Sync on confirms
  function syncedSubstrate() {
    return {
      ...substrateRef.current,
      clientLabel: nameRef?.current?.getValue(),
      substrateDescription: descriptionRef?.current?.getValue()
    }
  }

  function getUpdatedQuote() {
    const updatedSubstrate = syncedSubstrate()
    return updateSubstrate({
      quote,
      substrate: updatedSubstrate
    })
  }

  function handleNavArrowClick(itemKey: string) {
    onConfirm(getUpdatedQuote(), itemKey)
  }

  function onComplete() {
    onConfirm(getUpdatedQuote())
  }

  const handleDialogCancel = () => {
    const updatedQuote = getUpdatedQuote()
    const originalItem = findItem({ quote, options, key: substrateKey, section }) as UpdateableSubstrate
    const updatedItem = findItem({ quote: updatedQuote, options, key: substrateKey, section }) as UpdateableSubstrate

    onCancel(updatedQuote, !isLooselyEqual(originalItem, updatedItem))
  }

  return (
    <Dialog open={open} onClose={handleDialogCancel} maxWidth="md" fullWidth={true} {...baseDialogProps}>
      <DialogTitle loading={loading}>{`Edit ${objectLabels.substrate.value}`}</DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        <form
          id="edit-substrate-dialog"
          onSubmit={(e) => {
            e.preventDefault()
            onComplete()
          }}
          onKeyDown={(keyEvent) => {
            if ((keyEvent.charCode || keyEvent.keyCode) === 13 && !(keyEvent.target as any).editor) {
              onComplete()
            }
          }}
        >
          <Grid container={true} direction="column" spacing={3}>
            {splitCustom && (
              <Alert severity="warning">
                This {objectLabels.substrate.value} has already been customized in another section and can only be
                customized in one section at at time.
              </Alert>
            )}
            <Grid item xs={12}>
              <OverridableTextArea
                label={'Name'}
                disabled={disabled}
                value={substrateRef.current.clientLabel}
                classes={{ editorContent: classes.labelInput }}
                ref={nameRef}
              />
            </Grid>
            <Grid item xs={12}>
              <OverridableTextArea
                label={'Description'}
                disabled={disabled}
                value={substrateRef.current.substrateDescription}
                classes={{ htmlEditor: classes.htmlEditor }}
                allowFormatting
                ref={descriptionRef}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={handleDialogCancel} variant="textNoPadding" className={classes.backButton}>
            {cancelButtonLabel || 'Cancel'}
          </Button>
        }
        arrows={
          <DialogActionArrows
            hidden={!previousItemKey && !nextItemKey}
            onPrevious={previousItemKey ? () => handleNavArrowClick(previousItemKey) : null}
            onNext={nextItemKey ? () => handleNavArrowClick(nextItemKey) : null}
          />
        }
      >
        <Button
          type="submit"
          form="edit-substrate-dialog"
          variant={'contained'}
          disabled={splitCustom}
          onClick={() => {
            onComplete()
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditSubstrateDialog
